// src/pages/notifications/Notifications.js - Refactorizado con datos reales de dispositivos
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  LinearProgress,
  Card,
  CardContent,
  Grid,
  Badge,
  Alert
} from '@mui/material';
import { 
  Add as AddIcon, 
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Smartphone as SmartphoneIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Computer as ComputerIcon,
  DevicesOther as DevicesOtherIcon,
  Error
} from '@mui/icons-material';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc, 
  Timestamp,
  where 
} from 'firebase/firestore';
import { db } from '../../../firebase';
import { createTheme, ThemeProvider } from '@mui/material/styles';

// Paleta de colores verde pastel y violeta metalizado
const colorPalette = {
  primary: '#9CDBA6',        // Verde pastel principal
  primaryDark: '#7FB069',    // Verde pastel oscuro
  primaryLight: '#B8E5C1',   // Verde pastel claro
  secondary: '#9C8AC7',      // Violeta metalizado
  secondaryDark: '#8A7AB8',  // Violeta metalizado oscuro
  secondaryLight: '#B5A8D6', // Violeta metalizado claro
  accent: '#A8E6CF',         // Verde menta
  success: '#81C784',        // Verde éxito
  warning: '#FFB74D',        // Ámbar
  error: '#E57373',          // Rojo suave
  background: '#F8FDF9',     // Fondo verde muy claro
  surface: '#FFFFFF',        // Blanco
  text: '#2E3B3A',          // Verde oscuro para texto
  textSecondary: '#6B7B7A'   // Verde gris para texto secundario
};

// Tema personalizado
const customTheme = createTheme({
  palette: {
    primary: {
      main: colorPalette.primary,
      dark: colorPalette.primaryDark,
      light: colorPalette.primaryLight,
    },
    secondary: {
      main: colorPalette.secondary,
      dark: colorPalette.secondaryDark,
      light: colorPalette.secondaryLight,
    },
    background: {
      default: colorPalette.background,
      paper: colorPalette.surface,
    },
    text: {
      primary: colorPalette.text,
      secondary: colorPalette.textSecondary,
    },
    success: {
      main: colorPalette.success,
    },
    warning: {
      main: colorPalette.warning,
    },
    error: {
      main: colorPalette.error,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
      color: colorPalette.text,
    },
    subtitle2: {
      fontWeight: 500,
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colorPalette.surface,
          boxShadow: '0 4px 12px rgba(156, 219, 166, 0.15)',
          borderRadius: 12,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 4px 12px rgba(156, 138, 199, 0.3)',
          '&:hover': {
            boxShadow: '0 6px 16px rgba(156, 138, 199, 0.4)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          fontWeight: 500,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(127, 176, 105, 0.12)',
        },
      },
    },
  },
});

const Notifications = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [notificationStats, setNotificationStats] = useState({});
  const [deviceStats, setDeviceStats] = useState({
    total: 0,
    android: 0,
    ios: 0,
    web: 0,
    active: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [notificationToDelete, setNotificationToDelete] = useState(null);
  const [updatingVisibility, setUpdatingVisibility] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      await fetchDeviceStats();
      await fetchNotifications();
    };
    loadData();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = notifications.filter(
        notification =>
          notification.título?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          notification.descripción?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          getCategoryName(notification.tipo).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredNotifications(filtered);
    } else {
      setFilteredNotifications(notifications);
    }
    setPage(0);
  }, [searchTerm, notifications]);

  // Nueva función para obtener estadísticas de dispositivos
  const fetchDeviceStats = async () => {
    try {
      console.log('🔄 Obteniendo estadísticas de dispositivos...');
      
      // Obtener todos los usuarios con sus tokens
      const usersQuery = query(collection(db, 'users'));
      const usersSnapshot = await getDocs(usersQuery);
      
      let totalTokens = 0;
      let androidCount = 0;
      let iosCount = 0;
      let webCount = 0;
      let activeCount = 0;
      
      usersSnapshot.forEach((doc) => {
        const userData = doc.data();
        
        // Verificar si el usuario tiene tokens de notificación
        if (userData.expoTokens && Array.isArray(userData.expoTokens)) {
          userData.expoTokens.forEach((tokenObj) => {
            // Contar solo tokens con deviceInfo (que indican dispositivos reales)
            if (tokenObj.deviceInfo) {
              totalTokens++;
              
              // Contar por plataforma
              const platform = tokenObj.deviceInfo.platform?.toLowerCase();
              switch (platform) {
                case 'android':
                  androidCount++;
                  break;
                case 'ios':
                  iosCount++;
                  break;
                case 'web':
                  webCount++;
                  break;
              }
              
              // Contar activos (tokens que no están marcados como inactivos)
              if (tokenObj.active !== false) {
                activeCount++;
              }
            }
          });
        }
      });
      
      const stats = {
        total: totalTokens,
        android: androidCount,
        ios: iosCount,
        web: webCount,
        active: activeCount
      };
      
      console.log('📊 Estadísticas de dispositivos:', stats);
      setDeviceStats(stats);
      
    } catch (error) {
      console.error('❌ Error obteniendo estadísticas de dispositivos:', error);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, 'notifications'), 
        orderBy('createdAt', 'desc')
      );
      const querySnapshot = await getDocs(q);
      const notificationsData = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        notificationsData.push({ 
          id: doc.id, 
          ...data,
          // Convertir timestamps si es necesario
          createdAt: data.createdAt?.toDate?.() || data.createdAt,
          sentAt: data.sentAt?.toDate?.() || data.sentAt,
          fecha: data.fecha?.toDate?.() || data.fecha || data.createdAt?.toDate?.() || new Date()
        });
      });
      
      setNotifications(notificationsData);
      setFilteredNotifications(notificationsData);
      
      // Cargar estadísticas de lectura para cada notificación
      await fetchNotificationStats(notificationsData);
      
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotificationStats = async (notificationsData) => {
    try {
      const stats = {};
      
      for (const notification of notificationsData) {
        // Obtener total de usuarios que deberían recibir la notificación
        const totalUsers = await getTotalTargetUsers(notification);
        
        // Obtener usuarios que han leído la notificación
        const readUsers = await getReadUsers(notification.id);
        
        stats[notification.id] = {
          totalUsers,
          readUsers: readUsers.length,
          readPercentage: totalUsers > 0 ? Math.round((readUsers.length / totalUsers) * 100) : 0,
          sentCount: notification.pushStats?.pushNotificationsSent || 0
        };
      }
      
      setNotificationStats(stats);
    } catch (error) {
      console.error('Error fetching notification stats:', error);
    }
  };

  const getTotalTargetUsers = async (notification) => {
    try {
      const filters = notification.filters || {};
      
      switch (filters.targetType) {
        case 'all':
          // Contar todos los usuarios con tokens activos
          return deviceStats.active || 0;
          
        case 'course':
          // Contar usuarios con hijos en los cursos especificados
          if (notification.cursos && notification.cursos.length > 0) {
            const childrenQuery = query(
              collection(db, 'children'),
              where('curso', 'in', notification.cursos)
            );
            const childrenSnapshot = await getDocs(childrenQuery);
            const parentIds = new Set();
            childrenSnapshot.forEach(doc => {
              const data = doc.data();
              if (data.parentId) parentIds.add(data.parentId);
            });
            return parentIds.size;
          }
          return 0;
          
        case 'user':
          return 1; // Usuario específico
          
        default:
          return notification.pushStats?.targetTokens || deviceStats.active || 0;
      }
    } catch (error) {
      console.error('Error calculating target users:', error);
      return 0;
    }
  };

  const getReadUsers = async (notificationId) => {
    try {
      const readQuery = query(
        collection(db, 'readReceipts'),
        where('notificationId', '==', notificationId)
      );
      const readSnapshot = await getDocs(readQuery);
      const readUsers = [];
      
      readSnapshot.forEach(doc => {
        readUsers.push(doc.data().userId);
      });
      
      return readUsers;
    } catch (error) {
      console.error('Error fetching read users:', error);
      return [];
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchNotifications(),
      fetchDeviceStats()
    ]);
    setRefreshing(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCreateNotification = () => {
    navigate('/admin/notifications/create');
  };

  const handleEditNotification = (id) => {
    navigate(`/admin/notifications/${id}`);
  };

  const handleDeleteDialogOpen = (notification) => {
    setNotificationToDelete(notification);
    setDeleteDialogOpen(true);
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setNotificationToDelete(null);
  };

  const handleDeleteNotification = async () => {
    if (!notificationToDelete) return;

    try {
      await deleteDoc(doc(db, 'notifications', notificationToDelete.id));
      setNotifications(notifications.filter(item => item.id !== notificationToDelete.id));
      setDeleteDialogOpen(false);
      setNotificationToDelete(null);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleToggleVisibility = async (notification) => {
    setUpdatingVisibility(notification.id);
    try {
      const newVisibility = !notification.visible;
      
      await updateDoc(doc(db, 'notifications', notification.id), {
        visible: newVisibility
      });
      
      setNotifications(notifications.map(item => 
        item.id === notification.id ? { ...item, visible: newVisibility } : item
      ));
    } catch (error) {
      console.error('Error updating notification visibility:', error);
    } finally {
      setUpdatingVisibility(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Fecha desconocida';
    
    const dateObj = date instanceof Date ? date : 
                   date instanceof Timestamp ? date.toDate() :
                   date.seconds ? new Date(date.seconds * 1000) :
                   new Date(date);
    
    return dateObj.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryName = (tipo) => {
    const categories = {
      'urgent': 'Urgente',
      'academic': 'Académico',
      'activities': 'Actividades',
      'shop': 'Tienda',
      'curso': 'Específico del curso',
      'general': 'General'
    };
    return categories[tipo] || 'General';
  };

  const getCategoryColor = (tipo) => {
    const colors = {
      'urgent': 'error',
      'academic': 'success', 
      'activities': 'primary',
      'shop': 'secondary',
      'curso': 'warning',
      'general': 'default'
    };
    return colors[tipo] || 'default';
  };

  const getReadingProgressColor = (percentage) => {
    if (percentage >= 80) return colorPalette.success;
    if (percentage >= 50) return colorPalette.warning;
    return colorPalette.error;
  };

  const getPlatformIcon = (platform, count) => {
    const iconProps = { fontSize: 40, mb: 1 };
    switch (platform) {
      case 'android':
        return <AndroidIcon sx={{ ...iconProps, color: '#4CAF50' }} />;
      case 'ios':
        return <AppleIcon sx={{ ...iconProps, color: '#2196F3' }} />;
      case 'web':
        return <ComputerIcon sx={{ ...iconProps, color: colorPalette.secondary }} />;
      default:
        return <SmartphoneIcon sx={{ ...iconProps, color: colorPalette.primary }} />;
    }
  };

  const totalNotifications = filteredNotifications.length;
  const visibleNotifications = filteredNotifications.filter(n => n.visible).length;
  const totalSent = Object.values(notificationStats).reduce((sum, stat) => sum + stat.sentCount, 0);

  return (
    <ThemeProvider theme={customTheme}>
      <Box sx={{ 
        flexGrow: 1, 
        bgcolor: colorPalette.background, 
        minHeight: '100vh',
        p: 3 
      }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          mb: 4 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <NotificationsIcon sx={{ fontSize: 32, color: colorPalette.primary }} />
            <Typography variant="h4" sx={{ color: colorPalette.text }}>
              Gestión de Notificaciones
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={refreshing ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ color: colorPalette.secondary }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateNotification}
              sx={{ 
                bgcolor: colorPalette.secondary,
                '&:hover': { bgcolor: colorPalette.secondaryDark }
              }}
            >
              Nueva Notificación
            </Button>
          </Box>
        </Box>

        {/* Estadísticas generales y de dispositivos */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Estadísticas de notificaciones */}
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <NotificationsIcon sx={{ fontSize: 40, color: colorPalette.primary, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {totalNotifications}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Notificaciones
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <VisibilityIcon sx={{ fontSize: 40, color: colorPalette.success, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {visibleNotifications}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Visibles
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <PeopleIcon sx={{ fontSize: 40, color: colorPalette.secondary, mb: 1 }} />
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {totalSent}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Enviadas
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Estadísticas de dispositivos */}
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {getPlatformIcon('total', deviceStats.total)}
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {deviceStats.total}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Dispositivos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {getPlatformIcon('android', deviceStats.android)}
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {deviceStats.android}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Android
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                {getPlatformIcon('ios', deviceStats.ios)}
                <Typography variant="h4" sx={{ color: colorPalette.text, fontWeight: 600 }}>
                  {deviceStats.ios}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  iOS
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Información adicional de dispositivos */}
        {deviceStats.total > 0 && (
          <Card sx={{ mb: 4, bgcolor: colorPalette.primaryLight }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <Typography variant="h6" sx={{ color: colorPalette.text, mb: 1 }}>
                    📱 Dispositivos Registrados
                  </Typography>
                  <Typography variant="body2" sx={{ color: colorPalette.textSecondary }}>
                    Total de {deviceStats.total} dispositivos con tokens de notificación activos.
                    {deviceStats.web > 0 && ` Incluye ${deviceStats.web} dispositivos web.`}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-around', alignItems: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <AndroidIcon sx={{ color: '#4CAF50', fontSize: 24 }} />
                      <Typography variant="caption" display="block">
                        {deviceStats.android}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <AppleIcon sx={{ color: '#2196F3', fontSize: 24 }} />
                      <Typography variant="caption" display="block">
                        {deviceStats.ios}
                      </Typography>
                    </Box>
                    {deviceStats.web > 0 && (
                      <Box sx={{ textAlign: 'center' }}>
                        <ComputerIcon sx={{ color: colorPalette.secondary, fontSize: 24 }} />
                        <Typography variant="caption" display="block">
                          {deviceStats.web}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Tabla de notificaciones */}
        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por título, descripción o categoría..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: colorPalette.primary }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorPalette.primary,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: colorPalette.secondary,
                  },
                },
              }}
            />
          </Box>

          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: colorPalette.primaryLight }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Notificación
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Categoría
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Fecha
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Estado
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Estadísticas
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, color: colorPalette.text }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <CircularProgress sx={{ color: colorPalette.primary }} />
                        <Typography variant="body2" sx={{ mt: 2, color: colorPalette.textSecondary }}>
                          Cargando notificaciones...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredNotifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Box sx={{ py: 4 }}>
                        <NotificationsIcon sx={{ fontSize: 48, color: colorPalette.textSecondary, mb: 2 }} />
                        <Typography variant="body1" sx={{ color: colorPalette.textSecondary }}>
                          {searchTerm ? 'No se encontraron notificaciones' : 'No hay notificaciones aún'}
                        </Typography>
                        {!searchTerm && (
                          <Button 
                            variant="contained" 
                            onClick={handleCreateNotification}
                            sx={{ mt: 2, bgcolor: colorPalette.primary }}
                          >
                            Crear primera notificación
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredNotifications
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((notification) => {
                      const stats = notificationStats[notification.id] || {
                        totalUsers: 0,
                        readUsers: 0,
                        readPercentage: 0,
                        sentCount: 0
                      };

                      return (
                        <TableRow 
                          key={notification.id}
                          sx={{ 
                            '&:hover': { 
                              bgcolor: colorPalette.background,
                              transition: 'background-color 0.2s ease'
                            }
                          }}
                        >
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 0.5 }}>
                                {notification.título}
                              </Typography>
                              <Typography 
                                variant="body2" 
                                color="textSecondary" 
                                sx={{ 
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  maxWidth: 300
                                }}
                              >
                                {notification.descripción}
                              </Typography>
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Chip 
                              label={getCategoryName(notification.tipo)}
                              color={getCategoryColor(notification.tipo)} 
                              size="small"
                              sx={{ fontWeight: 500 }}
                            />
                          </TableCell>
                          
                          <TableCell>
                            <Typography variant="body2">
                              {formatDate(notification.fecha)}
                            </Typography>
                          </TableCell>
                          
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip 
                                label={notification.visible ? "Visible" : "Oculta"}
                                color={notification.visible ? "success" : "default"}
                                size="small"
                                sx={{ fontWeight: 500 }}
                              />
                              {notification.status && (
                                <Chip 
                                  label={notification.status}
                                  color={notification.status === 'sent' ? 'success' : 'warning'}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <PeopleIcon sx={{ fontSize: 16, color: colorPalette.textSecondary }} />
                                <Typography variant="body2">
                                  {stats.readUsers}/{stats.totalUsers} leídas
                                </Typography>
                                <Chip 
                                  label={`${stats.readPercentage}%`}
                                  size="small"
                                  sx={{ 
                                    bgcolor: getReadingProgressColor(stats.readPercentage),
                                    color: 'white',
                                    fontWeight: 600,
                                    fontSize: 11
                                  }}
                                />
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={stats.readPercentage} 
                                sx={{ 
                                  height: 6,
                                  borderRadius: 3,
                                  bgcolor: 'rgba(0,0,0,0.1)',
                                  '& .MuiLinearProgress-bar': {
                                    backgroundColor: getReadingProgressColor(stats.readPercentage),
                                    borderRadius: 3,
                                  }
                                }}
                              />
                              {stats.sentCount > 0 && (
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
                                  {stats.sentCount} enviadas
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                              <Tooltip title="Editar notificación">
                                <IconButton 
                                  onClick={() => handleEditNotification(notification.id)}
                                  size="small"
                                  sx={{ color: colorPalette.secondary }}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title={notification.visible ? "Ocultar notificación" : "Mostrar notificación"}>
                                <IconButton 
                                  onClick={() => handleToggleVisibility(notification)}
                                  size="small"
                                  disabled={updatingVisibility === notification.id}
                                  sx={{ color: notification.visible ? colorPalette.primary : colorPalette.textSecondary }}
                                >
                                  {updatingVisibility === notification.id ? (
                                    <CircularProgress size={20} />
                                  ) : notification.visible ? (
                                    <VisibilityIcon />
                                  ) : (
                                    <VisibilityOffIcon />
                                  )}
                                </IconButton>
                              </Tooltip>
                              
                              <Tooltip title="Eliminar notificación">
                                <IconButton 
                                  onClick={() => handleDeleteDialogOpen(notification)}
                                  size="small"
                                  sx={{ color: colorPalette.error }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filteredNotifications.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
            labelDisplayedRows={({ from, to, count }) => 
              `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
            sx={{
              borderTop: 1,
              borderColor: 'divider',
              '& .MuiTablePagination-toolbar': {
                bgcolor: colorPalette.background,
              },
            }}
          />
        </Paper>

        {/* Diálogo de confirmación de eliminación */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteDialogClose}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              bgcolor: colorPalette.surface,
            }
          }}
        >
          <DialogTitle sx={{ color: colorPalette.text, fontWeight: 600 }}>
            ¿Eliminar notificación?
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Esta acción no se puede deshacer
            </Alert>
            <DialogContentText sx={{ color: colorPalette.textSecondary }}>
              ¿Estás seguro de que quieres eliminar la notificación{' '}
              <strong>"{notificationToDelete?.título}"</strong>?
              {notificationStats[notificationToDelete?.id]?.sentCount > 0 && (
                <Typography variant="body2" sx={{ mt: 1, color: colorPalette.error }}>
                  Esta notificación ya fue enviada a{' '}
                  {notificationStats[notificationToDelete?.id]?.sentCount} dispositivos.
                </Typography>
              )}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button 
              onClick={handleDeleteDialogClose}
              variant="outlined"
              sx={{ color: colorPalette.textSecondary }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleDeleteNotification} 
              variant="contained"
              color="error"
              autoFocus
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>

        {/* Alertas informativas */}
        {filteredNotifications.length > 0 && (
          <Box sx={{ mt: 2 }}>
            {notifications.some(n => n.status === 'failed') && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.error }
                }}
              >
                Algunas notificaciones fallaron al enviarse. Revisa los detalles en la tabla.
              </Alert>
            )}
            
            {notifications.some(n => n.status === 'pending') && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.secondary }
                }}
              >
                Hay notificaciones pendientes de envío.
              </Alert>
            )}

            {deviceStats.total === 0 && (
              <Alert 
                severity="warning" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.warning }
                }}
              >
                <Typography variant="body2">
                  <strong>No hay dispositivos registrados.</strong><br />
                  Las notificaciones push no se pueden enviar hasta que los usuarios abran la aplicación móvil y acepten las notificaciones.
                </Typography>
              </Alert>
            )}

            {deviceStats.total > 0 && deviceStats.active < deviceStats.total && (
              <Alert 
                severity="info" 
                sx={{ 
                  mb: 2,
                  borderRadius: 2,
                  '& .MuiAlert-icon': { color: colorPalette.secondary }
                }}
              >
                <Typography variant="body2">
                  Hay {deviceStats.total - deviceStats.active} dispositivos inactivos que no recibirán notificaciones push.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        {/* Información adicional sobre dispositivos */}
        {deviceStats.total > 0 && (
          <Card sx={{ mt: 2, bgcolor: colorPalette.background }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: colorPalette.text }}>
                📊 Resumen de Dispositivos Registrados
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Distribución por plataforma:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    {deviceStats.android > 0 && (
                      <Chip 
                        icon={<AndroidIcon />}
                        label={`Android: ${deviceStats.android}`}
                        sx={{ bgcolor: '#E8F5E8', color: '#2E7D32' }}
                      />
                    )}
                    {deviceStats.ios > 0 && (
                      <Chip 
                        icon={<AppleIcon />}
                        label={`iOS: ${deviceStats.ios}`}
                        sx={{ bgcolor: '#E3F2FD', color: '#1565C0' }}
                      />
                    )}
                    {deviceStats.web > 0 && (
                      <Chip 
                        icon={<ComputerIcon />}
                        label={`Web: ${deviceStats.web}`}
                        sx={{ bgcolor: colorPalette.secondaryLight, color: colorPalette.secondaryDark }}
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Estado de dispositivos:</strong>
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label={`Activos: ${deviceStats.active}`}
                      sx={{ bgcolor: '#E8F5E8', color: '#2E7D32' }}
                    />
                    {(deviceStats.total - deviceStats.active) > 0 && (
                      <Chip 
                        icon={<Error />}
                        label={`Inactivos: ${deviceStats.total - deviceStats.active}`}
                        sx={{ bgcolor: '#FFEBEE', color: '#C62828' }}
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Typography variant="caption" sx={{ mt: 2, display: 'block', color: colorPalette.textSecondary }}>
                Los dispositivos se registran automáticamente cuando los usuarios abren la aplicación y aceptan las notificaciones push.
                Los tokens inactivos se limpian automáticamente después de 30 días sin actividad.
              </Typography>
            </CardContent>
          </Card>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default Notifications;